/**
 * Tableau Settings provider.
 *
 * Persists data inside the workbook itself via the Extensions Settings API, so
 * combinations & favorites travel with the .twbx and are shared by everyone who
 * opens the dashboard. Settings are string key/value pairs with a practical size
 * limit, so this provider is best for favorites + a bounded set of top
 * combinations rather than an unbounded audit log.
 *
 * `settings.saveAsync()` is debounced by the caller (store) to avoid excessive
 * workbook writes.
 */

import type {
  Combination,
  ExtensionConfig,
  Favorite,
  UsageEvent,
} from "@/types";
import { STORAGE_KEYS, type StorageProvider } from "./types";

function getSettings(): Tableau.Settings {
  const t = globalThis.tableau;
  if (!t?.extensions?.settings) {
    throw new Error("Tableau Settings API unavailable (not running in Tableau)");
  }
  return t.extensions.settings;
}

export class TableauSettingsProvider implements StorageProvider {
  readonly kind = "tableau-settings";
  private dirty = false;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  async init(): Promise<void> {
    getSettings(); // throws if unavailable
  }

  private read<T>(key: string, fallback: T): T {
    try {
      const raw = getSettings().get(key);
      return raw ? (JSON.parse(raw) as T) : fallback;
    } catch {
      return fallback;
    }
  }

  /** Write + debounce a workbook save. Multiple writes within 600ms coalesce. */
  private write<T>(key: string, value: T): void {
    getSettings().set(key, JSON.stringify(value));
    this.dirty = true;
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => void this.flush(), 600);
  }

  async flush(): Promise<void> {
    if (!this.dirty) return;
    await getSettings().saveAsync();
    this.dirty = false;
  }

  async getCombinations(): Promise<Combination[]> {
    return this.read<Combination[]>(STORAGE_KEYS.combinations, []);
  }
  async putCombination(combination: Combination): Promise<void> {
    const all = await this.getCombinations();
    const idx = all.findIndex((c) => c.id === combination.id);
    if (idx >= 0) all[idx] = combination;
    else all.push(combination);
    this.write(STORAGE_KEYS.combinations, all);
  }
  async deleteCombination(id: string): Promise<void> {
    this.write(STORAGE_KEYS.combinations, (await this.getCombinations()).filter((c) => c.id !== id));
  }

  async getUsageEvents(): Promise<UsageEvent[]> {
    // Cap the in-workbook audit log so we stay within settings size limits.
    return this.read<UsageEvent[]>(STORAGE_KEYS.events, []);
  }
  async appendUsageEvent(event: UsageEvent): Promise<void> {
    const all = await this.getUsageEvents();
    all.push(event);
    // Keep only the most recent 5,000 events in-workbook.
    const trimmed = all.slice(-5000);
    this.write(STORAGE_KEYS.events, trimmed);
  }

  async getFavorites(): Promise<Favorite[]> {
    return this.read<Favorite[]>(STORAGE_KEYS.favorites, []);
  }
  async putFavorite(favorite: Favorite): Promise<void> {
    const all = await this.getFavorites();
    const idx = all.findIndex((f) => f.id === favorite.id);
    if (idx >= 0) all[idx] = favorite;
    else all.push(favorite);
    this.write(STORAGE_KEYS.favorites, all);
  }
  async deleteFavorite(id: string): Promise<void> {
    this.write(STORAGE_KEYS.favorites, (await this.getFavorites()).filter((f) => f.id !== id));
  }

  async getConfig(): Promise<Partial<ExtensionConfig> | null> {
    return this.read<Partial<ExtensionConfig> | null>(STORAGE_KEYS.config, null);
  }
  async putConfig(config: ExtensionConfig): Promise<void> {
    this.write(STORAGE_KEYS.config, config);
  }

  async clearAll(): Promise<void> {
    const s = getSettings();
    s.erase(STORAGE_KEYS.combinations);
    s.erase(STORAGE_KEYS.events);
    s.erase(STORAGE_KEYS.favorites);
    this.dirty = true;
    await this.flush();
  }
}
